import type {
	ControlSize,
	ResponsiveClassMap,
	ResponsiveProperty,
} from "./types";

export const HEX_COLOR = /^#([\dA-Fa-f]{3,4}|[\dA-Fa-f]{6}|[\dA-Fa-f]{8})$/;

/**
 * Size for the confirm/cancel actions in `ManagedDialog` and `ConfirmDialog`.
 *
 * Both are shared by the whole app, so the touch target cannot be decided per
 * route: one `ManagedDialog` in `root.tsx` serves every `alert` / `confirm` /
 * `prompt` in the product, and the recovery `ConfirmDialog` reaches the phone
 * WebView only because it is mounted by the editor layout the reviewer renders
 * under. The split that matters is not which route but which input device, so
 * it is drawn at the `sm` breakpoint instead: 48px for a thumb below 640px,
 * unchanged 40px for a mouse at 640px and above.
 *
 * Shared between the two components because a dialog should not change size
 * depending on which of them happened to render it.
 */
export const dialogActionSize: ResponsiveProperty<ControlSize> = [
	"large",
	"medium",
];

/**
 * Common size variant related classNames for buttons, inputs, selects etc.
 */
export const controlSizeClassMap: ResponsiveClassMap<ControlSize> = {
	xs: {
		default: "min-h-[32px] px-2 gap-1 text-xs",
		sm: "sm:min-h-[32px] sm:px-2 sm:gap-1 sm:text-xs",
		md: "md:min-h-[32px] md:px-2 md:gap-1 md:text-xs",
		lg: "lg:min-h-[32px] lg:px-2 lg:gap-1 lg:text-xs",
		xl: "xl:min-h-[32px] xl:px-2 xl:gap-1 xl:text-xs",
		"2xl": "2xl:min-h-[32px] 2xl:px-2 2xl:gap-1 2xl:text-xs",
	},

	small: {
		default: "min-h-[36px] px-3 gap-2 text-[13px]",
		sm: "sm:min-h-[36px] sm:px-3 sm:gap-2 sm:text-[13px]",
		md: "md:min-h-[36px] md:px-3 md:gap-2 md:text-[13px]",
		lg: "lg:min-h-[36px] lg:px-3 lg:gap-2 lg:text-[13px]",
		xl: "xl:min-h-[36px] xl:px-3 xl:gap-2 xl:text-[13px]",
		"2xl": "2xl:min-h-[36px] 2xl:px-3 2xl:gap-2 2xl:text-[13px]",
	},

	medium: {
		default: "min-h-[40px] px-3 gap-2 text-sm",
		sm: "sm:min-h-[40px] sm:px-3 sm:gap-2 sm:text-sm",
		md: "md:min-h-[40px] md:px-3 md:gap-2 md:text-sm",
		lg: "lg:min-h-[40px] lg:px-3 lg:gap-2 lg:text-sm",
		xl: "xl:min-h-[40px] xl:px-3 xl:gap-2 xl:text-sm",
		"2xl": "2xl:min-h-[40px] 2xl:px-3 2xl:gap-2 2xl:text-sm",
	},

	large: {
		default: "min-h-[48px] px-3.5 gap-3 text-base",
		sm: "sm:min-h-[48px] sm:px-3.5 sm:gap-3 sm:text-base",
		md: "md:min-h-[48px] md:px-3.5 md:gap-3 md:text-base",
		lg: "lg:min-h-[48px] lg:px-3.5 lg:gap-3 lg:text-base",
		xl: "xl:min-h-[48px] xl:px-3.5 xl:gap-3 xl:text-base",
		"2xl": "2xl:min-h-[48px] 2xl:px-3.5 2xl:gap-3 2xl:text-base",
	},

	xl: {
		default: "min-h-[56px] px-4 gap-3 text-lg",
		sm: "sm:min-h-[56px] sm:px-4 sm:gap-3 sm:text-lg",
		md: "md:min-h-[56px] md:px-4 md:gap-3 md:text-lg",
		lg: "lg:min-h-[56px] lg:px-4 lg:gap-3 lg:text-lg",
		xl: "xl:min-h-[56px] xl:px-4 xl:gap-3 xl:text-lg",
		"2xl": "2xl:min-h-[56px] 2xl:px-4 2xl:gap-3 2xl:text-lg",
	},
};

export const DEFAULT_TOAST_LIFETIME = 5000;

export const A_VERY_VERY_VERY_VERY_LARGE_NUMBER = 99_999_999_999; // https://github.com/lun-energy/web-main/pull/576#discussion_r1634714312
